import { Dispatch, SetStateAction } from 'react';
import { Input, RecordInterface } from '@components/common/Forms';
import { useTranslation } from 'next-i18next';

/** Props */
interface JobWorkPlacementProps {
  record: RecordInterface,
  inputName?: string,
  setIsRemote?: Dispatch<SetStateAction<boolean>>,
  saveAddresses?: () => void,
}

/** Workplace types */
const workplaceTypes = [
  { // Office
    label: 'job.add.jobType.placeholder',
    record: {
      id: 'workPlacement_0',
      defaultValue: 'OFFICE',
      defaultChecked: true,
    },
  },
  { // Partial remote
    label: 'job.add.label.partial.remote',
    record: {
      id: 'workPlacement_1',
      defaultValue: 'PARTIAL_REMOTE',
    },
  },
  { // Partial remote
    label: 'job.add.label.remote',
    record: {
      id: 'workPlacement_2',
      defaultValue: 'REMOTE',
    },
  },
];

/**
 * @description Job Work Placement component. To be used in a form.
 * - `record` - record function from useBjForm
 * - `inputName` - name of the input; default is 'workPlacement'
 * - `setIsRemote` - set remote state
 * - `saveAddresses` - save addresses. Only used when remote is selected because we need to save the addresses.
 */
export const JobWorkPlacement = (props: JobWorkPlacementProps) => {
  // Destructure props
  const {
    record,
    inputName = 'workPlacement',
    setIsRemote,
    saveAddresses,
  } = props;

  // Translation
  const { t } = useTranslation();

  // Helpers
  const containerClass = 'mt-2 inline-flex rounded-full bg-surface-100 p-0.5 text-sm md:mx-3.5';
  const labelClass = 'cursor-pointer text-primary rounded-full px-4.5 py-1 text-center transition-all duration-200 peer-checked:bg-surface peer-checked:text-ink';

  // Render
  // **************************
  return (
    <div className={containerClass}>
      {workplaceTypes.map((workplaceType) => {
        const { label, record: workplaceRecord } = workplaceType;
        return (
          <label
            key={workplaceRecord.id}
            htmlFor={workplaceRecord.id}
            className="flex"
          >
            <Input
              type="radio"
              className="peer hidden"
              onChange={() => {
                if (setIsRemote) {
                  if (saveAddresses && workplaceRecord.defaultValue === 'REMOTE') saveAddresses();
                  setIsRemote(workplaceRecord.defaultValue === 'REMOTE');
                }
              }}
              {...record(inputName, workplaceRecord)}
            />
            <span className={labelClass}>
              {t(label)}
            </span>
          </label>
        );
      })}
    </div>
  );
};
